import { PaymentStatus } from '@repo/common';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { toast } from 'sonner';

import { useAppDispatch, useAppSelector } from '../../../features';
import { SingleCustomer } from '../../../features/slice/customers/customer.schema';
import { useLazyGetSingleCustomerQuery } from '../../../features/slice/customers/customer-api-slice';
import { closeModal } from '../../../features/slice/event/event-slice';
import {
    useGetAllOrdersQuery,
    usePayForMultiOrderMutation,
} from '../../../features/slice/order';
import { OrderSchema } from '../../../features/slice/order/order.schema';
import { useDebounce } from '../../../hooks/use-debounce';
import { Customer } from './utils/mockApi';

interface PaymentContextType {
    customers: Customer[];
    selectedCustomer: string;
    duePayments: OrderSchema['data'];
    selectedDues?: string[];
    paymentMethod: 'cash' | 'mobile';
    totalDueAmount?: number;
    dueLoading: boolean;
    setSelectedCustomer: (customerId: string) => void;
    handleDueSelection: (dueId: string, totalDue: number) => void;
    setPaymentMethod: (method: 'cash' | 'mobile') => void;
    handleCustomerChange: (customerId: string) => void;
    handlePaymentAmountChange: (dueId: string, amount: string) => void;
    handleSubmit: () => void;
    totalAmount: number;
    paymentAmounts: any;
    multiOrderLoading: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export function PaymentProvider({ children }: { children: ReactNode }) {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<string>('');
    const [selectedDues, setSelectedDues] = useState<string[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<'cash' | 'mobile'>(
        'cash',
    );
    const [getCustomerApi] = useLazyGetSingleCustomerQuery();
    const { modalData } = useAppSelector((state) => state.eventSlice);
    const [paymentAmounts, setPaymentAmounts] = useState<
        { orderId: string; amount: number }[]
    >([]);
    const [payForMultiOrderApi, { isLoading: multiOrderLoading }] =
        usePayForMultiOrderMutation();
    const dispatch = useAppDispatch();
    const { data: duePayments, isLoading: dueLoading } = useGetAllOrdersQuery(
        {
            page: 1,
            limit: 100,
            payment_status: PaymentStatus.DUE,
            customer_id: selectedCustomer,
        },
        {
            skip: !selectedCustomer,
        },
    );

    useEffect(() => {
        if (Array.isArray(modalData)) {
            setCustomers(
                modalData.map((data: SingleCustomer['data']) => ({
                    id: data.id,
                    name: data.name,
                })),
            );
        } else if (modalData && typeof modalData === 'object') {
            setSelectedCustomer(modalData.customer.id);
        }
    }, [modalData]);

    const handleDueSelection = (dueId: string, _totalDue: number) => {
        setSelectedDues((prev) =>
            prev.includes(dueId)
                ? prev.filter((id) => id !== dueId)
                : [...prev, dueId],
        );
        setPaymentAmounts((prev) =>
            prev.some((p) => p.orderId === dueId)
                ? prev.filter((p) => p.orderId !== dueId)
                : [...prev, { orderId: dueId, amount: 0 }],
        );
    };

    const handleCustomerChange = (customerId: string) => {
        setSelectedCustomer(customerId);
        getCustomerDuePayments(customerId);
    };

    const debounceAmount = useDebounce((dueId: string, value: string) => {
        const amount = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;

        const dueEntry = duePayments?.data?.find((due) => due.id === dueId);
        const maxPayableAmount = dueEntry
            ? (dueEntry.payment?.total_price ?? 0) -
              (dueEntry.payment?.total_paid ?? 0)
            : 0;

        if (amount > maxPayableAmount) {
            toast.error(
                `আপনি মোট ডিউ ${maxPayableAmount} টাকার বেশি পরিশোধ করতে পারবেন না!`,
            );
        } else {
            setPaymentAmounts((prev) =>
                prev.map((p) =>
                    p.orderId === dueId ? { ...p, amount: amount } : p,
                ),
            );
        }
    }, 500);

    const getCustomerDuePayments = async (id: string) => {
        return await getCustomerApi(id).unwrap();
    };

    const handlePaymentAmountChange = (dueId: string, value: string) => {
        debounceAmount(dueId, value);
    };

    const onSubmitPayments = async () => {
        const res = await payForMultiOrderApi({
            orders: paymentAmounts,
        }).unwrap();
        if (res) {
            toast.success('Payment Done!', {
                style: {
                    background: 'green',
                    color: 'white',
                },
            });
            dispatch(closeModal());
        }
    };

    const getPaymentAmount = useCallback(() => {
        const totalPayment =
            duePayments?.data?.reduce(
                (sum, order) => sum + (order.payment?.total_price ?? 0),
                0,
            ) ?? 0;
        const totalPaid =
            duePayments?.data?.reduce(
                (sum, order) => sum + (order.payment?.total_paid ?? 0),
                0,
            ) ?? 0;
        const currentPaid = paymentAmounts.reduce(
            (sum, entry) => sum + entry.amount,
            0,
        );
        return totalPayment - totalPaid - currentPaid;
    }, [paymentAmounts, duePayments]);

    return (
        <PaymentContext.Provider
            value={{
                customers,
                selectedCustomer,
                duePayments: duePayments?.data ?? [],
                selectedDues,
                paymentMethod,
                totalAmount: getPaymentAmount(),
                setSelectedCustomer,
                handleDueSelection,
                setPaymentMethod,
                handleCustomerChange,
                dueLoading,
                handlePaymentAmountChange,
                handleSubmit: onSubmitPayments,
                paymentAmounts,
                multiOrderLoading,
            }}
        >
            {children}
        </PaymentContext.Provider>
    );
}

export function usePayment() {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error(
            'useDuePayment must be used within a DuePaymentProvider',
        );
    }
    return context;
}
